/**
 * Normalize a full pathname down to its app + left-nav section.
 *
 * The app switcher restores the section a user was last in, not the exact page.
 * Deep links go stale (a prospect detail id may no longer resolve) and each app
 * already redirects its section root to a sensible default sub-route.
 *
 *   /prospecting/prospects/<uuid> -> /prospecting/prospects
 *   /prospecting/heatmap/map      -> /prospecting/heatmap
 *   /prospecting                  -> /prospecting
 *   /                             -> ''
 */
export function toNavSectionPath(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return '';
  return `/${segments.slice(0, 2).join('/')}`;
}

interface NavigationContext {
  from: string;
  to: string;
  timestamp: number;
  context?: any;
}

export const crossAppState = {
  // Save state before navigation
  saveState: (key: string, data: any): void => {
    try {
      sessionStorage.setItem(`commander_${key}`, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save state:', e);
    }
  },

  // Retrieve state after navigation
  getState: <T = any>(key: string): T | null => {
    try {
      const data = sessionStorage.getItem(`commander_${key}`);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to retrieve state:', e);
      return null;
    }
  },

  // Clear specific state
  clearState: (key: string): void => {
    sessionStorage.removeItem(`commander_${key}`);
  },

  // Save navigation context
  saveNavigationContext: (fromApp: string, toApp: string, context?: any): void => {
    const navContext: NavigationContext = {
      from: fromApp,
      to: toApp,
      timestamp: Date.now(),
      context,
    };
    sessionStorage.setItem('commander_nav_context', JSON.stringify(navContext));
  },

  // Get navigation context
  getNavigationContext: (): NavigationContext | null => {
    try {
      const data = sessionStorage.getItem('commander_nav_context');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to retrieve navigation context:', e);
      return null;
    }
  },

  // Save last visited route for an app
  saveLastRoute: (appId: string, route: string): void => {
    const routes = crossAppState.getLastRoutes() || {};
    routes[appId] = route;
    localStorage.setItem('commander_last_routes', JSON.stringify(routes));
  },

  // Get last visited routes for all apps
  getLastRoutes: (): Record<string, string> | null => {
    try {
      const data = localStorage.getItem('commander_last_routes');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to retrieve last routes:', e);
      return null;
    }
  },

  // Get last route for specific app
  getLastRoute: (appId: string): string | null => {
    const routes = crossAppState.getLastRoutes();
    return routes ? routes[appId] : null;
  },

  // Save user preferences
  savePreferences: (preferences: any): void => {
    localStorage.setItem('commander_preferences', JSON.stringify(preferences));
  },

  // Get user preferences
  getPreferences: <T = any>(): T | null => {
    try {
      const data = localStorage.getItem('commander_preferences');
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error('Failed to retrieve preferences:', e);
      return null;
    }
  },

  // Broadcast channel for real-time communication (optional)
  broadcast:
    typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('commander_apps') : null,

  // Send message to other apps
  sendMessage: (type: string, data: any): void => {
    if (crossAppState.broadcast) {
      crossAppState.broadcast.postMessage({ type, data, timestamp: Date.now() });
    }
  },

  // Listen for messages from other apps
  onMessage: (callback: (event: MessageEvent) => void): (() => void) | null => {
    if (crossAppState.broadcast) {
      crossAppState.broadcast.addEventListener('message', callback);
      return () => crossAppState.broadcast?.removeEventListener('message', callback);
    }
    return null;
  },
};
